import React, { useRef, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import ChevronLeft from 'yoda-core-components/lib/assets/svg/ChevronLeft.svg';
import ChevronRight from 'yoda-core-components/lib/assets/svg/ChevronRight.svg';
import DollarSVG from '../../../assets/images/Dollar.svg';
import { TIER_BENEFITS, termsAndConditionsLink } from '../../../common/Constants';

const TIERS = [
    {
        key: 'MEMBER',
        label: 'Member',
        spendLabel: 'Spend $0 – $99',
        titleAsterisk: null,
        spendAsterisk: null,
        dollarIcons: 1,
        cardBg: '#FFFFFF',
        textColor: '#202020',
    },
    {
        key: 'INSIDER',
        label: 'Insider',
        spendLabel: 'Spend $100',
        titleAsterisk: '*',
        spendAsterisk: '*',
        dollarIcons: 2,
        cardBg: '#D8D8D8',
        textColor: '#202020',
    },
    {
        key: 'ELITE',
        label: 'Elite',
        spendLabel: 'Spend $300',
        titleAsterisk: null,
        spendAsterisk: null,
        dollarIcons: 3,
        cardBg: '#373536',
        textColor: '#FFFFFF',
    },
    {
        key: 'VIP',
        label: 'VIP',
        spendLabel: 'Spend $1,000',
        titleAsterisk: '**',
        spendAsterisk: null,
        dollarIcons: 4,
        cardBg: '#CC0000',
        textColor: '#FFFFFF',
    },
];

const DollarIcon = () => (
    <Icon iconType="svg" width="24" height="24" viewBox="0 0 28 28">
        <DollarSVG />
    </Icon>
);

const TierBenefits = ({ currentTier = 'MEMBER', deviceType = {}, staticBase = '' }) => {
    const activeTier = currentTier.toUpperCase();
    const BackgroundPNG = `${staticBase}/assets/images/Background.png`;
    const CreditCardsPNG = `${staticBase}/assets/images/Credit Cards.png`;
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);
    const [breakpoint, setBreakpoint] = useState(() => {
        if (deviceType.isDesktop) return 'xl';
        if (deviceType.isTablet) return 'lg';
        return 'sm';
    });

    useEffect(() => {
        const compute = () => {
            const w = window.innerWidth;
            if (w >= 1280) setBreakpoint('xl');
            else if (w >= 1024) setBreakpoint('lg');
            else setBreakpoint('sm');
        };
        compute();
        window.addEventListener('resize', compute);
        return () => window.removeEventListener('resize', compute);
    }, []);

    const isXL = breakpoint === 'xl';
    const isLG = breakpoint === 'lg';

    const updateScrollState = () => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 0);
        setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    const scroll = (dir) => {
        const el = scrollRef.current;
        if (!el) return;
        el.scrollBy({
            left: dir === 'left' ? -el.clientWidth : el.clientWidth,
            behavior: 'smooth',
        });
        setTimeout(updateScrollState, 350);
    };

    const arrowBtn = (dir, visible) => (
        <button
            type="button"
            onClick={() => scroll(dir)}
            className={dt([
                'absolute',
                'top-1/2',
                '-translate-y-1/2',
                'z-10',
                'bg-white-overlay',
                'border-none',
                'rounded-half',
                'w-spacing-10',
                'h-spacing-10',
                'flex',
                'items-center',
                'justify-center',
                'cursor-pointer',
                'shadow-elevation1',
                dir === 'left' ? 'left-spacing-2' : 'right-spacing-2',
            ])}
            style={{
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
            }}
            aria-label={`Scroll ${dir}`}
        >
            <Icon iconType="svg" width="24" height="24" viewBox="0 0 40 40">
                {dir === 'left' ? <ChevronLeft /> : <ChevronRight />}
            </Icon>
        </button>
    );

    return (
        <div className={dt(['w-full', 'float-left', 'mt-6', 'mb-4'])}>
            {/* Heading matches screenshot: "The more you level up, the better the benefits." */}
            <h2
                className={dt(['font-open-sans', 'text-center', 'mb-spacing-4'])}
                style={{ fontSize: '24px', lineHeight: '36px', fontWeight: 400, color: '#CC0000' }}
            >
                The more you <span style={{ fontWeight: 700 }}>level up,</span> the{' '}
                <span style={{ fontWeight: 700 }}>better the benefits.</span>
            </h2>

            <div
                className={dt(['relative', 'w-full', 'py-spacing-4', 'min-h-[540px]'])}
                style={{ overflow: isLG ? 'hidden' : 'visible' }}
            >
                <img
                    src={BackgroundPNG}
                    alt=""
                    aria-hidden="true"
                    className={dt([
                        'absolute',
                        'inset-0',
                        'w-full',
                        'h-full',
                        'object-cover',
                        'z-0',
                        'pointer-events-none',
                    ])}
                />

                {isLG && arrowBtn('left', canScrollLeft)}
                {isLG && arrowBtn('right', canScrollRight)}

                <div
                    ref={scrollRef}
                    onScroll={updateScrollState}
                    className={dt([
                        'relative',
                        'z-[1]',
                        'flex',
                        'flex-row',
                        'items-start',
                        'gap-spacing-4',
                        '[scrollbar-width:none]',
                        '[-ms-overflow-style:none]',
                        'pt-[18px]',
                        'px-spacing-6',
                        'pb-1',
                    ])}
                    style={{ overflowX: isXL ? 'visible' : 'auto' }}
                >
                    {TIERS.map((tier) => {
                        const isActive = tier.key === activeTier;
                        const isDark = tier.cardBg !== '#FFFFFF' && tier.cardBg !== '#D8D8D8';
                        const cardWidth = isXL ? null : isLG ? '281px' : '208px';
                        const benefits = TIER_BENEFITS[tier.key];

                        return (
                            <div
                                key={tier.key}
                                className={dt([
                                    'rounded-lg',
                                    'overflow-hidden',
                                    'flex',
                                    'flex-col',
                                    'relative',
                                ])}
                                style={{
                                    backgroundColor: tier.cardBg,
                                    border: isActive ? '4px solid #202020' : 'none',
                                    ...(isXL
                                        ? {
                                              flex: 1,
                                              minWidth: 0,
                                              minHeight: isActive ? '498px' : '474px',
                                          }
                                        : {
                                              width: cardWidth,
                                              minWidth: cardWidth,
                                              height: isActive ? '498px' : '474px',
                                              flexShrink: 0,
                                          }),
                                    alignSelf: 'flex-start',
                                }}
                            >
                                {/* Card header */}
                                <div
                                    className={dt([
                                        'bg-black',
                                        'h-[69px]',
                                        'flex',
                                        'flex-col',
                                        'justify-center',
                                        'pl-spacing-4',
                                        'pr-spacing-2',
                                    ])}
                                >
                                    <div className={dt(['flex', 'items-center', 'gap-spacing-2'])}>
                                        <span
                                            className={dt([
                                                'font-open-sans-semibold',
                                                'text-2xlarge',
                                                'text-white',
                                            ])}
                                        >
                                            {tier.label}
                                            {tier.titleAsterisk && (
                                                <sup className={dt(['text-xs', '-mt-[2px]'])}>
                                                    {tier.titleAsterisk}
                                                </sup>
                                            )}
                                        </span>
                                        <div
                                            className={dt([
                                                'flex',
                                                'items-center',
                                                'gap-[2px]',
                                                'ml-spacing-2',
                                            ])}
                                        >
                                            {Array.from({ length: tier.dollarIcons }).map(
                                                (_, i) => (
                                                    // eslint-disable-next-line react/no-array-index-key
                                                    <DollarIcon key={i} />
                                                )
                                            )}
                                        </div>
                                    </div>
                                    <span
                                        className={dt([
                                            'font-open-sans',
                                            'text-small',
                                            'text-white',
                                            'mt-[2px]',
                                        ])}
                                    >
                                        {tier.spendLabel}
                                        {tier.spendAsterisk && (
                                            <sup className={dt(['text-[10px]'])}>
                                                {tier.spendAsterisk}
                                            </sup>
                                        )}
                                    </span>
                                </div>

                                {/* Benefits list */}
                                <div className={dt(['p-spacing-4', 'flex-1', 'overflow-y-auto'])}>
                                    <ul className={dt(['m-0', 'pl-spacing-4', 'list-disc'])}>
                                        {benefits.map((benefit) => (
                                            <li
                                                key={benefit}
                                                className={dt([
                                                    'font-open-sans-medium',
                                                    'text-small',
                                                    'mb-spacing-2',
                                                ])}
                                                style={{
                                                    color: isDark ? '#FFFFFF' : '#000000',
                                                    fontWeight: 500,
                                                }}
                                            >
                                                {benefit}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* YOUR TIER badge */}
                                {isActive && (
                                    <div
                                        className={dt([
                                            'bg-black',
                                            'h-spacing-6',
                                            'flex',
                                            'items-center',
                                            'justify-center',
                                            'text-white',
                                            'text-small',
                                        ])}
                                    >
                                        YOUR TIER
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* JCP CC Card Members section */}
            <div
                className={dt([
                    'bg-white',
                    'border-b',
                    'border-gray-25',
                    'py-spacing-6',
                    'flex',
                    'flex-col',
                    'gap-[10px]',
                    'mt-spacing-4',
                ])}
            >
                <div className={dt(['flex', 'flex-row', 'items-start', 'gap-spacing-4'])}>
                    <img
                        src={CreditCardsPNG}
                        alt="JCPenney Credit Cards"
                        className={dt(['shrink-0', 'w-[125px]', 'h-[38px]', 'object-contain'])}
                    />
                    <p
                        className={dt([
                            'text-small',
                            'font-normal',
                            'text-black',
                            'm-0',
                            'flex-1',
                            '[-letter-spacing:0.02em]',
                        ])}
                    >
                        *JCPenney Credit Cardmembers automatically qualify for Insider tier. **Must
                        be a JCPenney Credit Cardmember to qualify for VIP tier. Exclusions apply.
                        For details, visit{' '}
                        <a
                            href={termsAndConditionsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={dt(['underline', 'text-black'])}
                        >
                            Rewards Access Loyalty Program Terms &amp; Conditions
                        </a>
                        .
                    </p>
                </div>
            </div>
        </div>
    );
};

export { TierBenefits };
export default connect((state) => ({
    staticBase: state.context?.staticRoutes?.app || '',
}))(TierBenefits);
